import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table, Button, Icon, Popup, TextArea,
} from "semantic-ui-react";
import { Pie, Column } from "@ant-design/plots";
import jsPDF from "jspdf";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, ExportPdfButton, KPICard, YearBar, ChartBox, ChartLegend } from "../../../Shared";
import { renderDonutForPdf, renderColumnForPdf, buildStandardDashboardPdf, captureElementAtWidth, prepareKpiCardsForPdf, drawPill, drawProgressCell, hexToRgb } from "../../../Shared/pdfCharts";
import { ActionPlan, Sector, Area, User, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useCompany } from "../../../../contexts";
import { useSite } from "../../../../contexts/SiteContext";
import { CHART_PALETTE as CHART_COLORS } from "../../../../utils/chartTheme";
import "./ActionPlanDashboard.scss";

const actionPlanController = new ActionPlan();
const sectorController     = new Sector();
const areaController       = new Area();
const userController       = new User();
const siteCtrl              = new Site();

// ── Constants ──────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = CURRENT_YEAR - i;
  return { key: y, value: y, text: String(y) };
});

const STATUS_COLOR = {
  pending:              "#f9a825",
  in_progress:          "#1976d2",
  implemented_pending:  "#7b1fa2",
  closed:               "#43a047",
  overdue:              "#e53935",
  cancelled:            "#757575",
};

const GREEN_PALETTE = ["#7B9355", "#C4D693", "#A3B76B"];
const DONUT_PALETTE = ["#C4D693", "#A3B76B", "#8CCAFF", "#001936"];
const MONTH_BAR_STYLE = { radius: 5, maxWidth: 25, shadowColor: "transparent", shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0 };
const MONTH_BAR_SCALE_X = { padding: 0.08, paddingInner: 0.08, paddingOuter: 0.08 };
const MONTH_BAR_Y_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFontSize: 8,
  labelLineHeight: 8,
  labelFill: "#001936",
  labelOpacity: 1,
  gridStroke: "#D3D3D3",
  gridStrokeOpacity: 1,
  gridLineWidth: 1,
  gridLineDash: [5, 6],
};
const MONTH_BAR_X_AXIS = { labelFill: "#001936", labelOpacity: 1 };
const MONTH_BAR_INTERACTION = { tooltip: { crosshairs: false }, elementHighlightByX: false };

// Mismos 3 colores (Alto/Medio/Bajo) de la leyenda "Referencias".
const LEGEND_SWATCH_COLORS = {
  high: { blue: "#8CCAFF", green: "#B0C676" },
  mid:  { blue: "#BFD8ED", green: "#C8E087" },
  low:  { blue: "#DBEFFF", green: "#DDFCAD" },
};
const LEGEND_STEPS = [
  { key: "high", labelKey: "high" },
  { key: "mid",  labelKey: "medium" },
  { key: "low",  labelKey: "low" },
];
function apdHexToRgb(hex) {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}
const LEGEND_SCALE_RGB = {
  blue:  { low: apdHexToRgb(LEGEND_SWATCH_COLORS.low.blue),  mid: apdHexToRgb(LEGEND_SWATCH_COLORS.mid.blue),  high: apdHexToRgb(LEGEND_SWATCH_COLORS.high.blue) },
  green: { low: apdHexToRgb(LEGEND_SWATCH_COLORS.low.green), mid: apdHexToRgb(LEGEND_SWATCH_COLORS.mid.green), high: apdHexToRgb(LEGEND_SWATCH_COLORS.high.green) },
};
function legendPillStyle(val, max = 100, tone = "green") {
  const ratio = max > 0 ? Math.min(1, Math.max(0, val / max)) : 0;
  const { low, mid, high } = LEGEND_SCALE_RGB[tone];
  const [r, g, b] = ratio <= 0.5
    ? low.map((c, i) => Math.round(c + (mid[i] - c) * (ratio / 0.5)))
    : mid.map((c, i) => Math.round(c + (high[i] - c) * ((ratio - 0.5) / 0.5)));
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return { backgroundColor: `rgb(${r},${g},${b})`, color: brightness < 150 ? "#fff" : "#1a1c40" };
}

const EMPTY = {
  title: "", findingOrigin: "", findingType: "", findingDescription: "",
  sector: "", area: "", priority: "medium",
  responsible: "", plannedStartDate: "", dueDate: "", status: "pending",
  progress: 0, alertResponsible: null, observations: [],
};

// ── Helpers ─────────────────────────────────────────────────────────────────

const getId       = (val) => (val && typeof val === "object" ? val._id : val) || "";
const formatDate  = (d)   => d ? new Date(d).toLocaleDateString("es-AR") : "-";
const optLabel    = (opts, val) => opts.find((o) => o.value === val)?.label || val || "-";

function daysDiff(dueDate) {
  if (!dueDate) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
  return Math.round((due - now) / 86400000);
}

// ── Sub-components ───────────────────────────────────────────────────────────

const InlineSelect = ({ options, value, onChange, placeholder }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="apd__inline-select">
    <option value="">{placeholder || "-"}</option>
    {options.map((o) => <option key={o.key} value={o.value}>{o.label}</option>)}
  </select>
);

// Dropdown celeste de los filtros de arriba (Prioridad/Área) — mismo
// patrón que el resto de los dashboards/listados. `options` acepta
// {key,value,label} o {key,value,text}.
const FilterDropdown = ({ options, value, onChange, placeholder, clearable = false }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // El menú abre siempre hacia abajo — si el filtro queda cerca del borde
  // inferior de la pantalla, no entra y se corta contra el viewport. Si no
  // hay lugar debajo pero sí arriba, abre para arriba en su lugar.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuMaxHeight = 340;
      setOpenUp(window.innerHeight - rect.bottom < menuMaxHeight && rect.top > menuMaxHeight);
    }
    setOpen((v) => !v);
  };

  const selected = options.find((o) => String(o.value) === String(value));
  const textOf = (o) => o.text ?? o.label;
  const hasValue = clearable && Boolean(value);

  return (
    <div className="apd-fd" ref={ref}>
      <button type="button" className="apd-fd-toggle" onClick={toggleOpen}>
        <span className={selected ? "" : "apd-fd-placeholder"}>{selected ? textOf(selected) : placeholder}</span>
        {hasValue && (
          <span
            role="button"
            aria-label="clear"
            className="apd-fd-clear"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
          >
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5"/>
            </svg>
          </span>
        )}
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`apd-fd-arrow${open ? " apd-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className={`apd-fd-menu${openUp ? " apd-fd-menu--up" : ""}`}>
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`apd-fd-item${String(o.value) === String(value) ? " apd-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {textOf(o)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Dropdown multi-select (checkboxes) — mismo pill que FilterDropdown, para
// el filtro de Estado (varios estados a la vez), en vez de los checkboxes
// sueltos de antes.
const MultiSelectDropdown = ({ items, selected, onToggle, onClear, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Mismo flip que FilterDropdown: si no entra hacia abajo, abre para arriba.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuMaxHeight = 340;
      setOpenUp(window.innerHeight - rect.bottom < menuMaxHeight && rect.top > menuMaxHeight);
    }
    setOpen((v) => !v);
  };

  const activeCount = items.filter((i) => selected[i.key]).length;
  const isFiltered = activeCount > 0 && activeCount < items.length;
  const label = activeCount === 0 || activeCount === items.length
    ? placeholder
    : `${placeholder} (${activeCount})`;

  return (
    <div className="apd-fd" ref={ref}>
      <button type="button" className="apd-fd-toggle" onClick={toggleOpen}>
        <span className={activeCount === 0 ? "apd-fd-placeholder" : ""}>{label}</span>
        {isFiltered && onClear && (
          <span
            role="button"
            aria-label="clear"
            className="apd-fd-clear"
            onClick={(e) => { e.stopPropagation(); onClear(); setOpen(false); }}
          >
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5"/>
            </svg>
          </span>
        )}
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`apd-fd-arrow${open ? " apd-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className={`apd-fd-menu apd-fd-menu--checks${openUp ? " apd-fd-menu--up" : ""}`}>
          {items.map((i) => (
            <label key={i.key} className="apd-fd-check-item">
              <input
                type="checkbox"
                checked={!!selected[i.key]}
                onChange={() => onToggle(i.key)}
                style={{ accentColor: i.color }}
              />
              <span style={{ color: i.color }}>{i.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

export function ActionPlanDashboard({ reload, onReload, triggerNew = 0, companyName, siteName }) {
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const { site: contextSite } = useSite();
  const { company } = useCompany();
  const t = (key) => translations[key] || key;

  const PRIORITY_OPTIONS = [
    { key: "high",   value: "high",   label: t("priority_high"),   color: "#e53935" },
    { key: "medium", value: "medium", label: t("priority_medium"), color: "#f9a825" },
    { key: "low",    value: "low",    label: t("priority_low"),    color: "#43a047" },
  ];
  const STATUS_OPTIONS = [
    { key: "pending",              value: "pending",              label: t("ap_status_pending")             },
    { key: "in_progress",         value: "in_progress",         label: t("ap_status_in_progress")         },
    { key: "implemented_pending", value: "implemented_pending", label: t("ap_status_implemented_pending") },
    { key: "closed",              value: "closed",              label: t("ap_status_closed")              },
    { key: "overdue",             value: "overdue",             label: t("ap_status_overdue")             },
    { key: "cancelled",           value: "cancelled",           label: t("ap_status_cancelled")           },
  ];
  const ALERT_DAYS_OPTIONS = [
    { key: "7", value: 7, label: t("alert_days_7_before") },
    { key: "5", value: 5, label: t("alert_days_5_before") },
    { key: "3", value: 3, label: t("alert_days_3_before") },
    { key: "1", value: 1, label: t("alert_days_1_before") },
    { key: "0", value: 0, label: t("alert_same_day")      },
  ];
  const FINDING_ORIGIN_OPTIONS = [
    { key: "internal_audit",     value: "internal_audit",     label: t("origin_internal_audit")     },
    { key: "external_audit",     value: "external_audit",     label: t("origin_external_audit")     },
    { key: "plant_inspection",   value: "plant_inspection",   label: t("origin_plant_inspection")   },
    { key: "risk_assessment",    value: "risk_assessment",    label: t("origin_risk_assessment")    },
    { key: "accident_incident",  value: "accident_incident",  label: t("origin_accident_incident")  },
    { key: "legal_requirement",  value: "legal_requirement",  label: t("origin_legal_requirement")  },
    { key: "safety_checklist",   value: "safety_checklist",   label: t("origin_safety_checklist")   },
    { key: "new_project",        value: "new_project",        label: t("origin_new_project")        },
    { key: "initial_diagnosis",  value: "initial_diagnosis",  label: t("origin_initial_diagnosis")  },
    { key: "own_initiative",     value: "own_initiative",     label: t("origin_own_initiative")     },
    { key: "client_requirement", value: "client_requirement", label: t("origin_client_requirement") },
    { key: "other",              value: "other",              label: t("origin_other")              },
  ];
  const FINDING_TYPE_OPTIONS = [
    { key: "non_conformity",    value: "non_conformity",    label: t("finding_type_non_conformity")    },
    { key: "observation",       value: "observation",       label: t("finding_type_observation")       },
    { key: "improvement",       value: "improvement",       label: t("finding_type_improvement")       },
    { key: "preventive_action", value: "preventive_action", label: t("finding_type_preventive_action") },
    { key: "corrective_action", value: "corrective_action", label: t("finding_type_corrective_action") },
    { key: "legal_compliance",  value: "legal_compliance",  label: t("finding_type_legal_compliance")  },
  ];

  const isFirstRender = useRef(true);

  const [items,    setItems]    = useState(null);
  const [sectors,  setSectors]  = useState([]);
  const [areas,    setAreas]    = useState([]);
  const [catUsers, setCatUsers] = useState([]);
  const [companySiteIds, setCompanySiteIds] = useState([]);

  const noSiteInCtx    = !contextSite || contextSite === "0" || contextSite === 0;
  const noCompanyInCtx = !company     || company     === "0" || company     === 0;

  // Sitios de la empresa activa (para no mezclar datos de otras empresas
  // del mismo tenant cuando no hay un sitio puntual elegido).
  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, company, true)
      .then((res) => setCompanySiteIds((res || []).map((s) => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [accessToken, company, noCompanyInCtx]);

  const [year, setYear] = useState(CURRENT_YEAR);
  const kpiRef    = useRef();
  const tableRef  = useRef();
  const [exportingPdf, setExportingPdf] = useState(false);
  const exportingRef = useRef(false);

  // Filters
  const [search,         setSearch]         = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterArea,     setFilterArea]     = useState("");
  const [filterFrom,     setFilterFrom]     = useState("");
  const [filterTo,       setFilterTo]       = useState("");
  const [statusFilter,   setStatusFilter]   = useState({
    pending: true, in_progress: true, closed: true, overdue: true,
  });

  // Editing
  const [editingId,     setEditingId]     = useState(null);
  const [editingData,   setEditingData]   = useState(EMPTY);
  const [saving,        setSaving]        = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [newObsText,    setNewObsText]    = useState("");

  // Files (adjuntos de evidencia de cierre, dentro del panel de edición)
  const [uploading,    setUploading]    = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const fileInputRef = useRef(null);

  // Trigger new from parent
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (triggerNew > 0) startNew();
  }, [triggerNew]);

  useEffect(() => {
    if (!contextSite) return;
    userController.getUsersBySite(accessToken, contextSite, true)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setCatUsers(list.map((u) => ({
          key:   u._id,
          value: `${u.firstname || ""} ${u.lastname || ""}`.trim(),
          label: `${u.firstname || ""} ${u.lastname || ""}`.trim(),
        })));
      })
      .catch(() => setCatUsers([]));
  }, [contextSite, accessToken]);

  // Load catalogs
  useEffect(() => {
    (async () => {
      try {
        const [secs, ars] = await Promise.all([
          sectorController.getSectors(accessToken, true),
          areaController.getAreas(accessToken, true),
        ]);
        setSectors(secs.map((s) => ({ key: s._id, value: s._id, label: s.name })));
        setAreas(ars.map((a) => ({ key: a._id, value: a._id, label: a.name })));
      } catch {}
    })();
  }, []);

  // Load data
  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const res = await actionPlanController.getActionPlans(accessToken, true, company);
        setItems(res);
      } catch { setItems([]); }
    })();
  }, [reload, company]);

  // Filtered items
  const filteredItems = useMemo(() => {
    if (!items) return [];
    return items.filter((item) => {
      // Year filter (by dueDate or plannedStartDate)
      const itemYear = item.dueDate
        ? new Date(item.dueDate).getFullYear()
        : item.plannedStartDate
          ? new Date(item.plannedStartDate).getFullYear()
          : null;
      if (itemYear && itemYear !== year) return false;

      // Status checkboxes
      const statusKey = item.status === "overdue" || item.status === "overdue" ? "overdue"
        : item.status === "closed" ? "closed"
        : item.status === "in_progress" || item.status === "implemented_pending" ? "in_progress"
        : "pending";
      if (!statusFilter[statusKey]) return false;

      if (filterPriority && item.priority !== filterPriority) return false;

      const areaId = getId(item.area);
      if (filterArea && areaId !== filterArea) return false;

      if (filterFrom && item.plannedStartDate) {
        if (new Date(item.plannedStartDate) < new Date(filterFrom)) return false;
      }
      if (filterTo && item.plannedStartDate) {
        if (new Date(item.plannedStartDate) > new Date(filterTo)) return false;
      }

      if (search) {
        const q = search.toLowerCase();
        if (
          !item.title?.toLowerCase().includes(q) &&
          !item.responsible?.toLowerCase().includes(q) &&
          !item.findingDescription?.toLowerCase().includes(q)
        ) return false;
      }

      // Sitio/empresa activos: sitio puntual si hay uno elegido; si no, solo
      // los sitios de la empresa activa (nunca de otras empresas del tenant).
      const itemSite = getId(item.site);
      if (!noSiteInCtx) {
        if (itemSite !== contextSite) return false;
      } else if (!noCompanyInCtx && companySiteIds.length > 0) {
        if (!companySiteIds.includes(itemSite)) return false;
      }

      return true;
    });
  }, [items, statusFilter, filterPriority, filterArea, filterFrom, filterTo, search, year, contextSite, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  // KPIs
  const kpis = useMemo(() => {
    if (!items) return { total: 0, inProgress: 0, closed: 0, overdue: 0, leadTime: 0 };
    const base       = filteredItems;
    const total      = base.length;
    const inProgress = base.filter((i) => ["pending", "in_progress", "implemented_pending"].includes(i.status)).length;
    const closed     = base.filter((i) => i.status === "closed").length;
    const overdue    = base.filter((i) => {
      if (i.status === "closed") return false;
      const diff = daysDiff(i.dueDate);
      return diff !== null && diff < 0;
    }).length;
    const closedWithDates = base.filter((i) => i.status === "closed" && i.plannedStartDate && i.closedDate);
    const leadTime = closedWithDates.length
      ? Math.round(closedWithDates.reduce((sum, i) => {
          return sum + Math.abs(daysDiff(i.closedDate) - daysDiff(i.plannedStartDate));
        }, 0) / closedWithDates.length)
      : 0;
    return { total, inProgress, closed, overdue, leadTime };
  }, [filteredItems]);

  // ── Chart data ──────────────────────────────────────────────────────────────
  const MONTH_KEYS_APD = ["month_jan", "month_feb", "month_mar", "month_apr", "month_may", "month_jun", "month_jul", "month_aug", "month_sep", "month_oct", "month_nov", "month_dec"];

  const priorityChartData = useMemo(() => {
    const counts = {};
    filteredItems.forEach((i) => {
      const key = i.priority ? t(`priority_${i.priority}_short`) : t("apd_no_priority");
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [filteredItems]);

  const statusChartData = useMemo(() => {
    const counts = {};
    filteredItems.forEach((i) => {
      const key = i.status ? t(`ap_status_${i.status}`) : t("no_status");
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [filteredItems]);

  const monthChartData = useMemo(() => {
    const counts = Array(12).fill(0);
    const curYear = new Date().getFullYear();
    filteredItems.forEach((i) => {
      if (i.dueDate) {
        const d = new Date(i.dueDate);
        if (d.getFullYear() === curYear) counts[d.getMonth()]++;
      }
    });
    return MONTH_KEYS_APD.map((key, idx) => ({ mes: t(key), value: counts[idx] }));
  }, [filteredItems]);

  const hasMonthChartData = monthChartData.some((d) => d.value > 0);

  const apd_pieConfig = (data) => {
    const colors = data.map((_, i) => DONUT_PALETTE[i % DONUT_PALETTE.length]);
    const total  = data.reduce((sum, d) => sum + d.value, 0);
    return {
      data,
      angleField: "value",
      colorField: "type",
      innerRadius: 0.6,
      scale: { color: { range: colors } },
      label: false,
      legend: false,
      tooltip: {
        title: "type",
        items: [{
          field: "value",
          name: t("quantity"),
          valueFormatter: (v) => `${v} (${total > 0 ? ((v / total) * 100).toFixed(1) : "0.0"}%)`,
        }],
      },
      height: 250,
    };
  };

  const apd_colConfig = {
    data: monthChartData,
    xField: "mes",
    yField: "value",
    colorField: "mes",
    scale: { color: { range: GREEN_PALETTE }, x: MONTH_BAR_SCALE_X },
    legend: false,
    height: 250,
    style: MONTH_BAR_STYLE,
    axis: { x: MONTH_BAR_X_AXIS, y: MONTH_BAR_Y_AXIS },
    tooltip: { items: [{ channel: "y", name: t("apd_tooltip_actions") }] },
    interaction: MONTH_BAR_INTERACTION,
  };

  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportingPdf(true);
    const chartCleanups = [];
    try {
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const priorityColorMap = Object.fromEntries(priorityChartData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));
      const statusColorMap = Object.fromEntries(statusChartData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));

      const [priorityDonut, statusDonut, monthColumn] = await Promise.all([
        renderDonutForPdf(priorityChartData, priorityColorMap, { showPercentLabels: true }),
        renderDonutForPdf(statusChartData, statusColorMap, { showPercentLabels: true }),
        renderColumnForPdf(hasMonthChartData ? monthChartData : [], { xField: "mes", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25 }),
      ]);
      [priorityDonut, statusDonut, monthColumn].forEach((r) => { if (r.cleanup) chartCleanups.push(r.cleanup); });

      const kpiCanvas = await captureElementAtWidth(kpiRef.current, 1400, {}, prepareKpiCardsForPdf);

      const tableColumns = [
        // Con "Total Grupo" (sin sitio puntual elegido) los datos vienen de
        // varios sitios mezclados — sin esta columna no se puede saber a
        // cuál pertenece cada fila (igual que en la tabla en pantalla).
        ...(noSiteInCtx ? [{ key: "site", header: t("site"), flex: 1.2, value: (r) => r.site?.name || "-" }] : []),
        { key: "title", header: t("title"), flex: 2.6, value: (r) => r.title },
        { key: "area", header: t("area"), flex: 1.3, value: (r) => r.area?.name || "-" },
        { key: "responsible", header: t("responsible"), flex: 1.4, value: (r) => r.responsible || "-" },
        {
          key: "priority", header: t("priority"), flex: 1, align: "center",
          render: (doc, { x, y, width, height, row }) => {
            if (!row.priority) return;
            const opt = PRIORITY_OPTIONS.find((o) => o.value === row.priority);
            drawPill(doc, { x: x + width / 2, y: y + height / 2, text: t(`priority_${row.priority}_short`), color: opt?.color || "#888" });
          },
        },
        { key: "dueDate", header: t("due_date"), flex: 1.1, value: (r) => formatDate(r.dueDate) },
        {
          key: "status", header: t("status"), flex: 1.4,
          render: (doc, { x, y, width, height, row }) => {
            doc.setFontSize(8);
            doc.setFont(undefined, "normal");
            doc.setTextColor(...hexToRgb(STATUS_COLOR[row.status] || "#555"));
            doc.text(optLabel(STATUS_OPTIONS, row.status), x + 6, y + height / 2 + 3);
          },
        },
        {
          key: "progress", header: t("progress"), flex: 1.3,
          render: (doc, { x, y, width, height, row }) => {
            drawProgressCell(doc, { x, y, width, height, pct: row.progress || 0 });
          },
        },
      ];

      await buildStandardDashboardPdf({
        jsPDFCtor: jsPDF,
        pageTitle: `${t("action_plan_management")} — ${year}`,
        companyName: [companyName, siteName].filter(Boolean).join(" · "),
        generatedOnLabel: t("generated_on"),
        kpiCanvas,
        chartRows: [
          {
            height: 210,
            cards: [
              { title: t("apd_by_priority"), ...priorityDonut, legendPosition: "right" },
              { title: t("apd_by_status"), ...statusDonut, legendPosition: "right" },
              { title: t("apd_due_by_month"), ...monthColumn },
            ],
          },
        ],
        table: { columns: tableColumns, rows: filteredItems, rowHeight: 26, headerHeight: 24, emptyMessage: t("no_results") },
        tableTitle: t("actions"),
        filename: `planes_accion_${year}.pdf`,
      });
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      chartCleanups.forEach((fn) => fn());
      exportingRef.current = false;
      setExportingPdf(false);
    }
  };

  // ── Edit helpers ───────────────────────────────────────────────────────────

  const startNew = () => {
    setEditingId("new");
    setEditingData({ ...EMPTY });
    setActiveSection("finding");
    setNewObsText("");
  };

  const cancelEdit = () => {
    setEditingId(null); setEditingData(EMPTY);
    setActiveSection(null); setNewObsText("");
  };

  const setField = (field, value) => setEditingData((p) => ({ ...p, [field]: value }));

  const handleSave = async () => {
    if (!editingData.title) { toast.error(t("title_required")); return; }
    setSaving(true);
    try {
      if (editingId === "new") {
        await actionPlanController.createActionPlan(accessToken, editingData);
        toast.success(t("action_plan_created"));
      } else {
        await actionPlanController.updateActionPlan(accessToken, editingId, editingData);
        toast.success(t("action_plan_updated"));
      }
      cancelEdit();
      onReload();
    } catch (err) {
      toast.error(err?.msg || t("error_saving"));
    } finally { setSaving(false); }
  };

  const handleViewFile = async (fileKey) => {
    try {
      const url = await actionPlanController.getFile(accessToken, fileKey);
      if (url) window.open(url, "_blank");
      else toast.error(t("error_downloading_file"));
    } catch { toast.error(t("error_downloading_file")); }
  };

  // ── Section panel ──────────────────────────────────────────────────────────

  const SECTIONS = [
    { key: "finding",  label: t("finding")  },
    { key: "followup", label: t("followup") },
  ];

  const renderSectionPanel = () => {
    if (!activeSection || !editingId) return null;

    const panelContent = {
      finding: (
        <div className="apd__section-fields">
          <div className="apd__field-grid">
            <div className="apd__field-row">
              <label>{t("title")} *</label>
              <input type="text" value={editingData.title} onChange={(e) => setField("title", e.target.value)} className="apd__inline-input" />
            </div>
            <div className="apd__field-row">
              <label>{t("finding_origin")}</label>
              <InlineSelect options={FINDING_ORIGIN_OPTIONS} value={editingData.findingOrigin} onChange={(v) => setField("findingOrigin", v)} placeholder={t("select")} />
            </div>
            <div className="apd__field-row">
              <label>{t("finding_type")}</label>
              <InlineSelect options={FINDING_TYPE_OPTIONS} value={editingData.findingType} onChange={(v) => setField("findingType", v)} placeholder={t("select")} />
            </div>
            <div className="apd__field-row">
              <label>{t("sector")}</label>
              <InlineSelect options={sectors} value={editingData.sector} onChange={(v) => setField("sector", v)} placeholder={t("select")} />
            </div>
            <div className="apd__field-row">
              <label>{t("area")}</label>
              <InlineSelect options={areas} value={editingData.area} onChange={(v) => setField("area", v)} placeholder={t("select")} />
            </div>
            <div className="apd__field-row">
              <label>{t("priority")}</label>
              <InlineSelect options={PRIORITY_OPTIONS} value={editingData.priority} onChange={(v) => setField("priority", v)} />
            </div>
          </div>
          <div className="apd__field-row">
            <label>{t("finding_description")}</label>
            <textarea value={editingData.findingDescription} onChange={(e) => setField("findingDescription", e.target.value)} className="apd__inline-textarea" rows={4} />
          </div>
        </div>
      ),
      followup: (
        <div className="apd__section-fields">
          <div className="apd__field-grid">
            <div className="apd__field-row">
              <label>{t("responsible")}</label>
              <InlineSelect options={catUsers} value={editingData.responsible} onChange={(v) => setField("responsible", v)} placeholder={t("responsible")} />
            </div>
            <div className="apd__field-row">
              <label>{t("planned_start_date")}</label>
              <input type="date" value={editingData.plannedStartDate} onChange={(e) => setField("plannedStartDate", e.target.value)} className="apd__inline-input" />
            </div>
            <div className="apd__field-row">
              <label>{t("due_date")}</label>
              <input type="date" value={editingData.dueDate} onChange={(e) => setField("dueDate", e.target.value)} className="apd__inline-input" />
            </div>
            <div className="apd__field-row">
              <label>{t("status")}</label>
              <InlineSelect options={STATUS_OPTIONS} value={editingData.status} onChange={(v) => setField("status", v)} />
            </div>
            <div className="apd__field-row">
              <label>{t("progress")} (%)</label>
              <input type="number" min={0} max={100} value={editingData.progress} onChange={(e) => setField("progress", Number(e.target.value))} className="apd__inline-input" style={{ width: "80px" }} />
            </div>
            <div className="apd__field-row">
              <label>{t("alert_responsible")}</label>
              <div className="apd__alert-row">
                <label className="apd__toggle">
                  <input type="checkbox"
                    checked={editingData.alertResponsible !== null && editingData.alertResponsible !== undefined}
                    onChange={(e) => setField("alertResponsible", e.target.checked ? 7 : null)}
                  />
                  <span className="apd__toggle-slider" />
                </label>
                {editingData.alertResponsible !== null && editingData.alertResponsible !== undefined && (
                  <InlineSelect options={ALERT_DAYS_OPTIONS} value={editingData.alertResponsible}
                    onChange={(v) => setField("alertResponsible", Number(v))} placeholder="-" />
                )}
              </div>
            </div>
          </div>

          {/* Evidencia de cierre */}
          <div className="apd__field-row" style={{ marginTop: "0.5rem" }}>
            <label>{t("closing_evidence")}</label>
            {editingId === "new" ? (
              <p className="apd__evidence-hint">{t("save_first_to_attach")}</p>
            ) : (
              <div className="apd__evidence-section">
                {(() => {
                  const currentItem = items?.find((i) => i._id === editingId);
                  const files = currentItem?.files || [];
                  return files.length > 0 ? (
                    <ul className="apd__evidence-list">
                      {files.map((f, i) => (
                        <li key={i} className="apd__evidence-item">
                          <Icon name={/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? "image outline" : "file outline"} />
                          <span className="apd__evidence-name">{f.name}</span>
                          <Button circular icon size="mini" style={{ backgroundColor: "#648EBC", color: "#fff" }} onClick={() => handleViewFile(f.key)}>
                            <Icon name="eye" />
                          </Button>
                          <Button circular icon size="mini" style={{ backgroundColor: "#e57373", color: "#fff" }}
                            loading={deletingFile === f.key} disabled={deletingFile !== null}
                            onClick={async () => {
                              setDeletingFile(f.key);
                              try {
                                await actionPlanController.deleteFile(accessToken, editingId, f.key);
                                toast.success(t("file_deleted")); onReload();
                              } catch { toast.error(t("error_deleting")); }
                              finally { setDeletingFile(null); }
                            }}>
                            <Icon name="trash" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="apd__evidence-hint">{t("no_attached_files")}</p>;
                })()}
                <div className="apd__evidence-upload">
                  <input type="file" multiple ref={fileInputRef} />
                  <Button size="mini" primary loading={uploading} disabled={uploading}
                    onClick={async () => {
                      const files = fileInputRef.current?.files;
                      if (!files || files.length === 0) { toast.warning(t("select_files_to_attach")); return; }
                      setUploading(true);
                      try {
                        for (const file of Array.from(files)) {
                          const fd = new FormData(); fd.append("file", file);
                          await actionPlanController.uploadFile(accessToken, editingId, fd);
                        }
                        toast.success(t("files_uploaded"));
                        if (fileInputRef.current) fileInputRef.current.value = "";
                        onReload();
                      } catch { toast.error(t("error_uploading_files")); }
                      finally { setUploading(false); }
                    }}>
                    {t("upload")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div className="apd__field-row" style={{ marginTop: "0.5rem" }}>
            <label>{t("observations")}</label>
            {editingData.observations.length > 0 && (
              <div className="apd__obs-list">
                {[...editingData.observations].reverse().map((obs, i) => (
                  <div key={i} className="apd__obs-entry">
                    <div className="apd__obs-meta">
                      <span className="apd__obs-user">{obs.userName}</span>
                      <span className="apd__obs-date">{obs.createdAt ? new Date(obs.createdAt).toLocaleDateString() : ""}</span>
                      <Icon name="trash alternate" link color="red" style={{ marginLeft: "auto" }}
                        onClick={() => {
                          const idx = editingData.observations.length - 1 - i;
                          setField("observations", editingData.observations.filter((_, j) => j !== idx));
                        }} />
                    </div>
                    <div className="apd__obs-text">{obs.text}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="apd__obs-new">
              <textarea rows={2} value={newObsText} onChange={(e) => setNewObsText(e.target.value)}
                placeholder={t("enter_observations")} className="apd__inline-textarea" />
              <Button size="mini" primary disabled={!newObsText.trim()}
                onClick={() => {
                  const userName = [user?.name, user?.lastname].filter(Boolean).join(" ") || "Usuario";
                  setField("observations", [...editingData.observations, { text: newObsText.trim(), userName, createdAt: new Date() }]);
                  setNewObsText("");
                }}>
                {t("add")}
              </Button>
            </div>
          </div>
        </div>
      ),
    };

    return (
      <div className="apd__section-panel">
        <div className="apd__section-nav">
          {SECTIONS.map((s) => (
            <button key={s.key} type="button"
              className={`apd__section-btn${activeSection === s.key ? " active" : ""}`}
              onClick={() => setActiveSection(activeSection === s.key ? null : s.key)}
            >{s.label}</button>
          ))}
        </div>
        {activeSection && (
          <div className="apd__section-body">{panelContent[activeSection]}</div>
        )}
      </div>
    );
  };

  // ── Render table row ───────────────────────────────────────────────────────

  const renderEditRow = () => (
    <Table.Row key="editing" positive={editingId === "new"} warning={editingId !== "new"}>
      <Table.Cell style={{ minWidth: 200 }}>
        <input type="text" value={editingData.title} onChange={(e) => setField("title", e.target.value)}
          placeholder={t("title")} className="apd__inline-input" />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={areas} value={editingData.area} onChange={(v) => setField("area", v)} placeholder={t("area")} />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={catUsers} value={editingData.responsible} onChange={(v) => setField("responsible", v)} placeholder={t("responsible")} />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={PRIORITY_OPTIONS} value={editingData.priority} onChange={(v) => setField("priority", v)} />
      </Table.Cell>
      <Table.Cell>
        <input type="date" value={editingData.dueDate} onChange={(e) => setField("dueDate", e.target.value)} className="apd__inline-input" />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={STATUS_OPTIONS} value={editingData.status} onChange={(v) => setField("status", v)} />
      </Table.Cell>
      <Table.Cell>
        <input type="number" min={0} max={100} value={editingData.progress}
          onChange={(e) => setField("progress", Number(e.target.value))}
          className="apd__inline-input" style={{ width: 60 }} />
        <span style={{ marginLeft: 4, fontSize: "0.82rem" }}>%</span>
      </Table.Cell>
      <Table.Cell textAlign="center">—</Table.Cell>
      <Table.Cell textAlign="center" className="apd__actions">
        <Popup size="mini" content={t("save")} trigger={
          <Button icon size="mini" loading={saving} disabled={saving} onClick={handleSave} className="apd__save-btn">
            <Icon name="save" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}><Icon name="close" /></Button>
        } />
      </Table.Cell>
    </Table.Row>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  if (items === null) return <LoadingSpinner />;

  const areaOptions = areas;
  const priorityOptions = PRIORITY_OPTIONS;

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="apd">

      {/* ── Filtros — afuera de la card gris de KPIs, arriba de todo (igual
          que Inicio/Requisitos/Plan anual) ── */}
      <div className="apd__header">
        <YearBar year={year} options={YEAR_OPTIONS} onChange={setYear} />
        <ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} title={t("export_pdf")} />
      </div>

      {/* ── KPI Cards ── */}
      <div className="resumen-wrapper apd__kpi-wrapper">
        <div className="apd__kpis" ref={kpiRef}>
          <KPICard label={t("kpi_total")}             value={kpis.total}      />
          <KPICard label={t("kpi_in_progress_label")} value={kpis.inProgress} />
          <KPICard label={t("kpi_closed_label")}      value={kpis.closed}     />
          <KPICard label={t("kpi_overdue_label")}     value={kpis.overdue}    />
          <KPICard label={t("kpi_lead_time")}         value={kpis.leadTime}   />
        </div>
      </div>

      {/* ── Charts — mismo patrón que Inicio/Requisitos/Plan anual ── */}
      <div className="apd__charts">
        <div className="apd__chart-slot">
          <ChartBox title={t("apd_by_priority")}>
            {priorityChartData.length > 0 ? (
              <div className="apd__pie-row">
                <div className="chartWrapper apd__pie-wrapper">
                  <Pie {...apd_pieConfig(priorityChartData)} />
                </div>
                <ChartLegend items={priorityChartData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
              </div>
            ) : (
              <div className="chartWrapper apd__pie-wrapper"><div className="apd__empty-ring" /></div>
            )}
          </ChartBox>
        </div>

        <div className="apd__chart-slot">
          <ChartBox title={t("apd_by_status")}>
            {statusChartData.length > 0 ? (
              <div className="apd__pie-row">
                <div className="chartWrapper apd__pie-wrapper">
                  <Pie {...apd_pieConfig(statusChartData)} />
                </div>
                <ChartLegend items={statusChartData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
              </div>
            ) : (
              <div className="chartWrapper apd__pie-wrapper"><div className="apd__empty-ring" /></div>
            )}
          </ChartBox>
        </div>

        <div className="apd__chart-slot">
          <ChartBox title={t("apd_due_by_month")}>
            <div className="chartWrapper">
              {hasMonthChartData ? <Column {...apd_colConfig} /> : <div className="apd__chart-empty">{t("no_data")}</div>}
            </div>
          </ChartBox>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="apd__data-wrap">
      <div className="apd__filters">
        <div className="apd__search-wrap">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input className="apd__search-input" placeholder={`${t("search")}...`}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        {/* Estado — dropdown multi-select, en vez de los checkboxes sueltos */}
        <div className="apd__filter-select-wrap">
          <MultiSelectDropdown
            items={[
              { key: "pending",     color: "#f9a825", label: t("pending") },
              { key: "in_progress", color: "#1976d2", label: t("in_progress") },
              { key: "closed",      color: "#43a047", label: t("closed") },
              { key: "overdue",     color: "#e53935", label: t("overdue") },
            ]}
            selected={statusFilter}
            onToggle={(key) => setStatusFilter((p) => ({ ...p, [key]: !p[key] }))}
            onClear={() => setStatusFilter({ pending: true, in_progress: true, closed: true, overdue: true })}
            placeholder={t("status")}
          />
        </div>

        <div className="apd__filter-select-wrap">
          <FilterDropdown options={priorityOptions} value={filterPriority} onChange={setFilterPriority} placeholder={t("priority")} clearable />
        </div>

        <div className="apd__filter-select-wrap">
          <FilterDropdown options={areaOptions} value={filterArea} onChange={setFilterArea} placeholder={t("area")} clearable />
        </div>

        {/* Desde / Hasta — la etiqueta va dentro del campo */}
        <label className="apd__date-field">
          <span className="apd__date-field-label">{t("from")}</span>
          <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="apd__date-field-input" />
        </label>

        <label className="apd__date-field">
          <span className="apd__date-field-label">{t("to")}</span>
          <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="apd__date-field-input" />
        </label>
      </div>

      {/* ── Table ── */}
      <div className="apd__table-card" ref={tableRef}>
        {/* <h3 className="apd__table-title">{t("actions")}</h3> */}
        <div className="apd__wrapper">
          <Table className="apd__tbl unstackable rtable-cards">
            <Table.Header>
              <Table.Row>
                {noSiteInCtx && <Table.HeaderCell>{t("site")}</Table.HeaderCell>}
                <Table.HeaderCell>{t("title")}</Table.HeaderCell>
                <Table.HeaderCell>{t("area")}</Table.HeaderCell>
                <Table.HeaderCell>{t("responsible")}</Table.HeaderCell>
                <Table.HeaderCell>{t("priority")}</Table.HeaderCell>
                <Table.HeaderCell>{t("due_date")}</Table.HeaderCell>
                <Table.HeaderCell>{t("status")}</Table.HeaderCell>
                <Table.HeaderCell>{t("progress")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredItems.length === 0 && editingId !== "new" ? (
                <Table.Row>
                  <Table.Cell colSpan={noSiteInCtx ? 8 : 7} textAlign="center" style={{ color: "#999" }}>{t("no_results")}</Table.Cell>
                </Table.Row>
              ) : filteredItems.map((item) => (
                  <Table.Row key={item._id}>
                    {noSiteInCtx && <Table.Cell data-label={t("site")}>{item.site?.name || "-"}</Table.Cell>}

                    {/* Title */}
                    <Table.Cell data-label={t("title")} style={{ minWidth: 200, textAlign: "left" }}>
                      <div className="apd__title-cell">
                        <span className="apd__title-main">{item.title}</span>
                        {item.findingDescription && (
                          <span className="apd__title-desc">{item.findingDescription}</span>
                        )}
                        <span className="apd__title-dates">
                          {item.plannedStartDate && `${t("opening")}: ${formatDate(item.plannedStartDate)}`}
                          {item.status === "closed" && item.closedDate && ` • ${t("closed_date")}: ${formatDate(item.closedDate)}`}
                        </span>
                      </div>
                    </Table.Cell>

                    <Table.Cell data-label={t("area")}>{item.area?.name || "-"}</Table.Cell>
                    <Table.Cell data-label={t("responsible")}>{item.responsible || "-"}</Table.Cell>

                    {/* Priority badge */}
                    <Table.Cell data-label={t("priority")}>
                      {item.priority ? (
                        <span className="apd__priority-badge"
                          style={{ background: PRIORITY_OPTIONS.find((o) => o.value === item.priority)?.color || "#888" }}>
                          {t(`priority_${item.priority}_short`)}
                        </span>
                      ) : "-"}
                    </Table.Cell>

                    {/* Due date + overdue */}
                    <Table.Cell data-label={t("due_date")}>
                      <div className="apd__due-cell">
                        <span><Icon name="calendar outline" />{formatDate(item.dueDate)}</span>
                        {(() => {
                          const diff = daysDiff(item.dueDate);
                          if (diff === null || item.status === "closed") return null;
                          if (diff < 0) return (
                            <span className="apd__overdue-badge">
                              <Icon name="warning sign" />
                              {t("overdue_by")} {Math.abs(diff)} {t("days")}
                            </span>
                          );
                          return null;
                        })()}
                      </div>
                    </Table.Cell>

                    {/* Status */}
                    <Table.Cell data-label={t("status")}>
                      <span className="apd__row-pill" style={{ backgroundColor: STATUS_COLOR[item.status] || "#757575", color: "#fff" }}>
                        {optLabel(STATUS_OPTIONS, item.status)}
                      </span>
                    </Table.Cell>

                    {/* Progress */}
                    <Table.Cell data-label={t("progress")}>
                      <span className="apd__row-pill" style={legendPillStyle(item.progress || 0, 100, "green")}>
                        {item.progress || 0}%
                      </span>
                    </Table.Cell>
                  </Table.Row>
              ))}
              {editingId === "new" && renderEditRow()}
            </Table.Body>
          </Table>
        </div>

        {editingId && renderSectionPanel()}

        <p className="apd__tip">{t("action_plan_tip")}</p>
      </div>

      {/* Referencias — mismo patrón que Comparativa en Inicio. */}
      <div className="apd__legend">
        <span className="apd__legend-title">{t("references")}</span>
        <div className="apd__legend-row">
          {LEGEND_STEPS.map((step) => (
            <div key={step.key} className="apd__legend-group">
              <span className="apd__legend-label">{t(step.labelKey)}</span>
              <span className="apd__legend-swatches">
                {["blue", "green"].map((tone) => (
                  <span
                    key={tone}
                    className="apd__legend-swatch"
                    style={{ backgroundColor: LEGEND_SWATCH_COLORS[step.key][tone] }}
                  />
                ))}
              </span>
            </div>
          ))}
        </div>
      </div>
      </div>
      </div>
    </>
  );
}
